/* ==========================================================================
   A duration inked against the queue's own threshold ladder.

   Every duration on the C2 surfaces that answers "is this slow?" renders
   through here, so one ladder (commandModel.ts) drives one appearance in all
   four places it appears: the queue's `Latency` and `In state` cells, the
   lifecycle group headers, and the dispatch rail's standing-queue list.

   Two carriers, never colour alone (THE BAR §accessibility):

     colour   --text-mid → --text-hi → --warn-fg → --error-fg
     shape    nothing → nothing → a triangle → a block + a cell tint

   A greyscale reader still sees the breaching rows: a mark appears in the
   cell's leading gutter, which is otherwise empty, and the two breaching
   bands carry different marks. The gutter is a fixed 7px so the numbers stay
   aligned on their own edge whether or not a mark is drawn — a mark that
   shifted the value would break §8 tell #7.

   The band word is in the DOM as text for a screen reader, not only in the
   title, because a title on a span inside a row button is never announced.
   ========================================================================== */

import { duration, NO_VALUE } from '../../components/format.ts';
import type { Band } from './commandModel.ts';
import styles from './BandedAge.module.css';

/** What the mark asserts, spoken. Kept short — it prefixes the number. */
const SPOKEN: Record<Band, string> = {
  nominal: '',
  high: '',
  warn: 'past p95:',
  over: 'past ceiling:',
};

export function BandedAge({
  ms,
  band,
  note,
  className,
  compact,
  role,
}: {
  /** Null renders the em dash — never a zero standing in for "no sample". */
  ms: number | null;
  /** Null means the ladder does not apply: history, not rot. */
  band: Band | null;
  /** The sentence the band asserts, in the window's own numbers. */
  note: string;
  className?: string;
  /** `2h` rather than `2h 04m`, for the tightest slots. */
  compact?: true;
  /** `gridcell` where this span IS a cell of the queue's row grid. */
  role?: 'gridcell';
}) {
  const marked = band === 'warn' || band === 'over';
  return (
    <span
      className={className === undefined ? styles.age : `${styles.age} ${className}`}
      data-band={band ?? 'none'}
      title={note}
      role={role}
    >
      {marked && (
        <>
          <span className={styles.mark} aria-hidden="true" />
          <span className="f3-sr-only">{SPOKEN[band]} </span>
        </>
      )}
      <span className={styles.val}>
        {ms === null ? NO_VALUE : duration(ms, compact === true ? { compact: 1 } : {})}
      </span>
    </span>
  );
}
